import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import {
  normalizeIntegerString,
  PORTFOLIO_SIZE_OPTIONS,
  type PortfolioPricingApplicationInput,
  type PricingNeed,
  type SiteOwnershipType,
} from "@/lib/portfolioPricing";

type PortfolioPricingApplicationFormProps = {
  value: PortfolioPricingApplicationInput;
  onChange: <K extends keyof PortfolioPricingApplicationInput>(
    key: K,
    value: PortfolioPricingApplicationInput[K],
  ) => void;
  disabled?: boolean;
};

export const PortfolioPricingApplicationForm = ({
  value,
  onChange,
  disabled = false,
}: PortfolioPricingApplicationFormProps) => (
  <div className="grid gap-4 md:grid-cols-2">
    <div className="space-y-2">
      <Label htmlFor="business-name">Business / agency / organisation name</Label>
      <Input
        id="business-name"
        value={value.businessName}
        onChange={(e) => onChange("businessName", e.target.value)}
        placeholder="Example Agency"
        className="bg-muted/50"
        disabled={disabled}
      />
    </div>
    <div className="space-y-2">
      <Label htmlFor="company-url">Website / company URL</Label>
      <Input
        id="company-url"
        type="url"
        value={value.companyUrl}
        onChange={(e) => onChange("companyUrl", e.target.value)}
        placeholder="https://example.com"
        className="bg-muted/50"
        disabled={disabled}
      />
    </div>
    <div className="space-y-2">
      <Label htmlFor="role-account-type">Role / account type</Label>
      <Input
        id="role-account-type"
        value={value.roleAccountType}
        onChange={(e) => onChange("roleAccountType", e.target.value)}
        placeholder="Agency owner, developer, technical lead"
        className="bg-muted/50"
        disabled={disabled}
      />
    </div>
    <div className="space-y-2">
      <Label>Approximate portfolio size</Label>
      <Select
        value={value.approximatePortfolioSize}
        onValueChange={(nextValue) => onChange("approximatePortfolioSize", nextValue)}
        disabled={disabled}
      >
        <SelectTrigger className="bg-muted/50">
          <SelectValue placeholder="Select a range" />
        </SelectTrigger>
        <SelectContent>
          {PORTFOLIO_SIZE_OPTIONS.map((option) => (
            <SelectItem key={option} value={option}>
              {option}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-2">
      <Label htmlFor="current-site-count">How many websites do you manage now?</Label>
      <Input
        id="current-site-count"
        inputMode="numeric"
        value={value.currentSiteCount}
        onChange={(e) => onChange("currentSiteCount", normalizeIntegerString(e.target.value))}
        placeholder="25"
        className="bg-muted/50"
        disabled={disabled}
      />
    </div>
    <div className="space-y-2">
      <Label htmlFor="expected-site-count">How many do you expect to secure with ShadowScan?</Label>
      <Input
        id="expected-site-count"
        inputMode="numeric"
        value={value.expectedSiteCount}
        onChange={(e) => onChange("expectedSiteCount", normalizeIntegerString(e.target.value))}
        placeholder="10"
        className="bg-muted/50"
        disabled={disabled}
      />
    </div>
    <div className="space-y-2">
      <Label>Whose sites are these?</Label>
      <Select
        value={value.siteOwnershipType}
        onValueChange={(nextValue) => onChange("siteOwnershipType", nextValue as SiteOwnershipType)}
        disabled={disabled}
      >
        <SelectTrigger className="bg-muted/50">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="own">Our own sites</SelectItem>
          <SelectItem value="client">Client sites</SelectItem>
          <SelectItem value="mixed">A mix of both</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-2">
      <Label>Pricing path needed for</Label>
      <Select
        value={value.pricingNeed}
        onValueChange={(nextValue) => onChange("pricingNeed", nextValue as PricingNeed)}
        disabled={disabled}
      >
        <SelectTrigger className="bg-muted/50">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="internal">Internal sites</SelectItem>
          <SelectItem value="agency_client">Agency client sites</SelectItem>
          <SelectItem value="reseller_partner">Reseller / partner use</SelectItem>
        </SelectContent>
      </Select>
    </div>
    <div className="space-y-2">
      <Label htmlFor="primary-market">Country / primary market</Label>
      <Input
        id="primary-market"
        value={value.primaryMarket}
        onChange={(e) => onChange("primaryMarket", e.target.value)}
        placeholder="Australia, United States, UK"
        className="bg-muted/50"
        disabled={disabled}
      />
    </div>
    <div className="space-y-2">
      <Label>Expected onboarding timeframe</Label>
      <Select
        value={value.expectedOnboardingTimeframe}
        onValueChange={(nextValue) => onChange("expectedOnboardingTimeframe", nextValue)}
        disabled={disabled}
      >
        <SelectTrigger className="bg-muted/50">
          <SelectValue placeholder="Select timing" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="immediately">Immediately</SelectItem>
          <SelectItem value="this_month">This month</SelectItem>
          <SelectItem value="this_quarter">This quarter</SelectItem>
          <SelectItem value="planning">Still planning</SelectItem>
        </SelectContent>
      </Select>
    </div>

    <div className="space-y-2 md:col-span-2">
      <Label htmlFor="use-case-summary">Briefly describe how you plan to use ShadowScan</Label>
      <Textarea
        id="use-case-summary"
        value={value.useCaseSummary}
        onChange={(e) => onChange("useCaseSummary", e.target.value)}
        placeholder="Tell us how you manage these sites and what you want to evaluate."
        className="min-h-[110px] bg-muted/50"
        disabled={disabled}
      />
    </div>
  </div>
);
